import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API, auth, money, fmtDate, inputCls, Field, Modal, PageHead, PrimaryBtn, FinanceEmpty, StatusBadge, Paginator, PrintStyle, printArea, exportPdf, errMsg } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';
import PremiumDatePicker from '../../../components/ui/PremiumDatePicker';

const DISCOUNT_TYPES = { '': 'بلا خصم', percentage: 'نسبة %', fixed: 'مبلغ ثابت' };
const emptyItem = () => ({ description: '', quantity: 1, unit: '', unit_price: '' });
const formatIban = (iban) => (iban ? iban.replace(/(.{4})/g, '$1 ').trim() : '');

const emptyForm = (vat) => ({
  client_name: '', client_phone: '', client_email: '', client_tax_number: '',
  title: '', issue_date: new Date().toISOString().slice(0, 10), expiry_date: '',
  items: [emptyItem()], discount_type: '', discount_value: '',
  tax_enabled: !!vat.vat_registered, tax_rate: vat.vat_rate || 15, notes: '', terms: '', status: 'draft',
  show_bank_details: false, bank_account_id: '',
});

const computeTotals = (form) => {
  const subtotal = form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const discountValue = parseFloat(form.discount_value) || 0;
  let discountAmount = 0;
  if (form.discount_type === 'percentage') discountAmount = subtotal * (discountValue / 100);
  else if (form.discount_type === 'fixed') discountAmount = Math.min(discountValue, subtotal);
  const taxable = subtotal - discountAmount;
  const taxRate = form.tax_enabled ? (parseFloat(form.tax_rate) || 0) : 0;
  const taxAmount = form.tax_enabled ? taxable * (taxRate / 100) : 0;
  return { subtotal, discountAmount, taxable, taxAmount, total: taxable + taxAmount };
};

export default function QuotesPage() {
  const { club } = useOutletContext() || {};
  const printRef = useRef(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [vatSettings, setVatSettings] = useState({ vat_registered: false, vat_rate: 15 });
  const [clubInfo, setClubInfo] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm({ vat_registered: false, vat_rate: 15 }));
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewQuote, setViewQuote] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [delQuote, setDelQuote] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [res, settingsRes, bankRes] = await Promise.all([
        axios.get(`${API}/quotes?page=${page}&limit=15${statusFilter ? `&status=${statusFilter}` : ''}`, auth()),
        axios.get(`${API}/club-settings`, auth()).catch(() => null),
        axios.get(`${API}/bank-accounts`, auth()).catch(() => null),
      ]);
      setData(res.data);
      if (settingsRes?.data) {
        setVatSettings({ vat_registered: !!settingsRes.data.vat_registered, vat_rate: settingsRes.data.vat_rate || 15 });
        setClubInfo({
          name: settingsRes.data.club_name, logo_file_id: settingsRes.data.logo_file_id,
          phone: settingsRes.data.contact_info?.phone, email: settingsRes.data.contact_info?.email,
          city: settingsRes.data.club_city, vat_number: settingsRes.data.vat_number,
        });
      }
      if (Array.isArray(bankRes?.data)) setBankAccounts(bankRes.data.filter((a) => a.status === 'active'));
    } catch (e) { setError(e.response?.status === 403 ? 'لا تملك صلاحية الوصول لعروض الأسعار' : 'تعذر التحميل'); }
    setLoading(false);
  }, [page, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const totals = computeTotals(form);

  const openCreate = () => { setEditing(null); setForm(emptyForm(vatSettings)); setFormError(null); setModal(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      client_name: q.client?.name || '', client_phone: q.client?.phone || '', client_email: q.client?.email || '', client_tax_number: q.client?.tax_number || '',
      title: q.title, issue_date: q.issue_date, expiry_date: q.expiry_date,
      items: (q.items || []).map((i) => ({ ...i })), discount_type: q.discount_type || '', discount_value: q.discount_value || '',
      tax_enabled: !!q.tax_enabled, tax_rate: q.tax_rate || vatSettings.vat_rate || 15,
      notes: q.notes || '', terms: q.terms || '', status: q.status,
      show_bank_details: !!q.show_bank_details, bank_account_id: q.bank_account_id || '',
    });
    setFormError(null); setModal(true);
  };

  const toggleBankDetails = (checked) => {
    if (checked && !form.bank_account_id) {
      const defaultAcc = bankAccounts.find((a) => a.is_default) || bankAccounts[0];
      setForm({ ...form, show_bank_details: true, bank_account_id: defaultAcc?.id || '' });
    } else {
      setForm({ ...form, show_bank_details: checked });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.filter((i) => i.description && i.unit_price !== '');
    if (!form.client_name || !form.title || !form.issue_date || !form.expiry_date || items.length === 0) {
      setFormError('يرجى تعبئة العميل والعنوان والتواريخ وبند واحد على الأقل'); return;
    }
    if (form.expiry_date < form.issue_date) { setFormError('تاريخ الانتهاء يجب أن يكون بعد تاريخ الإصدار'); return; }
    if (form.show_bank_details && !form.bank_account_id) { setFormError('يرجى اختيار الحساب البنكي أو تعطيل خيار إظهار بيانات التحويل'); return; }
    setSaving(true); setFormError(null);
    const payload = {
      client: { name: form.client_name, phone: form.client_phone || null, email: form.client_email || null, tax_number: form.client_tax_number || null },
      title: form.title, issue_date: form.issue_date, expiry_date: form.expiry_date,
      items: items.map((i) => ({ description: i.description, quantity: parseFloat(i.quantity) || 1, unit: i.unit || null, unit_price: parseFloat(i.unit_price) })),
      discount_type: form.discount_type || null, discount_value: parseFloat(form.discount_value) || 0,
      tax_enabled: form.tax_enabled, tax_rate: form.tax_enabled ? (parseFloat(form.tax_rate) || 0) : null,
      notes: form.notes || null, terms: form.terms || null,
      show_bank_details: form.show_bank_details, bank_account_id: form.show_bank_details ? form.bank_account_id : null,
    };
    try {
      if (editing) await axios.put(`${API}/quotes/${editing.id}`, { ...payload, status: form.status }, auth());
      else await axios.post(`${API}/quotes`, payload, auth());
      setModal(false); notify(editing ? 'تم تحديث عرض السعر' : 'تم إنشاء عرض السعر'); load();
    } catch (err) { setFormError(errMsg(err, 'فشل الحفظ')); }
    setSaving(false);
  };

  const duplicate = async (q) => {
    try { await axios.post(`${API}/quotes/${q.id}/duplicate`, {}, auth()); notify('تم نسخ عرض السعر'); load(); }
    catch (err) { notify(errMsg(err, 'فشل النسخ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/quotes/${delQuote.id}`, auth()); notify('تم حذف عرض السعر'); setDelQuote(null); load(); }
    catch (err) { notify(errMsg(err, 'فشل الحذف')); setDelQuote(null); }
  };

  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="quotes-error">{error}</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="quotes-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="عروض الأسعار" desc="عروض أسعار مستقلة تماماً عن الفواتير — لا تُحتسب في الإيرادات أو المدفوعات أو إحصائيات لوحة التحكم"
        action={<PrimaryBtn onClick={openCreate} data-testid="quote-add-btn"><Icon name="plus" className="w-4 h-4" /> عرض سعر جديد</PrimaryBtn>} />

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-600" data-testid="quote-status-filter">
            <option value="">كل الحالات</option>
            <option value="draft">مسودة</option><option value="sent">مُرسل</option><option value="accepted">مقبول</option><option value="rejected">مرفوض</option><option value="expired">منتهي</option>
          </select>
          <span className="text-xs text-slate-400">الإجمالي: {data.total} عرض</span>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : data.items.length === 0 ? <div className="p-6"><FinanceEmpty text="لا توجد عروض أسعار — أنشئ أول عرض سعر" /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">الرقم</th><th className="px-4 py-2.5 font-semibold">العميل / الجهة</th>
                  <th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الإصدار</th><th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الانتهاء</th>
                  <th className="px-4 py-2.5 font-semibold">الإجمالي</th><th className="px-4 py-2.5 font-semibold">الحالة</th><th className="px-4 py-2.5 font-semibold">إجراءات</th>
                </tr></thead>
                <tbody>
                  {data.items.map((q) => (
                    <tr key={q.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="quote-row">
                      <td className="px-4 py-2.5 font-bold text-slate-800" dir="ltr">{q.quote_number}</td>
                      <td className="px-4 py-2.5 text-slate-700">{q.client?.name}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(q.issue_date)}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(q.expiry_date)}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-900" dir="rtl">{money(q.total)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={q.effective_status} /></td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setViewQuote(q)} title="عرض" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="quote-view-btn"><Icon name="document" className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(q)} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="quote-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                          <button onClick={() => duplicate(q)} title="نسخ" className="rounded-lg p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700" data-testid="quote-duplicate-btn"><Icon name="copy" className="w-4 h-4" /></button>
                          <button onClick={() => setDelQuote(q)} title="حذف" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="quote-delete-btn"><Icon name="trash" className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <Paginator page={page} total={data.total} limit={15} onChange={setPage} />
      </div>

      <Modal open={modal} title={editing ? `تعديل عرض السعر ${editing.quote_number}` : 'عرض سعر جديد'} onClose={() => setModal(false)} wide>
        {formError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="quote-form-error">{formError}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="اسم العميل / الجهة" required><input className={inputCls} value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="quote-input-client-name" /></Field>
            <Field label="عنوان عرض السعر" required><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="quote-input-title" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="جوال العميل"><input className={inputCls} dir="ltr" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} data-testid="quote-input-client-phone" /></Field>
            <Field label="بريد العميل"><input className={inputCls} dir="ltr" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="quote-input-client-email" /></Field>
            <Field label="الرقم الضريبي (اختياري)"><input className={inputCls} dir="ltr" value={form.client_tax_number} onChange={(e) => setForm({ ...form, client_tax_number: e.target.value })} data-testid="quote-input-client-tax" /></Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="تاريخ الإصدار" required><PremiumDatePicker value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} testId="quote-input-issue" /></Field>
            <Field label="تاريخ انتهاء الصلاحية" required><PremiumDatePicker value={form.expiry_date} onChange={(v) => setForm({ ...form, expiry_date: v })} testId="quote-input-expiry" /></Field>
            {editing && (
              <Field label="الحالة">
                <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="quote-input-status">
                  <option value="draft">مسودة</option><option value="sent">مُرسل</option><option value="accepted">مقبول</option><option value="rejected">مرفوض</option>
                </select>
              </Field>
            )}
          </div>

          <div className="space-y-2">
            <span className="text-xs font-semibold text-slate-600">البنود</span>
            {form.items.map((it, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input className={`${inputCls} min-w-[160px] flex-1`} placeholder="الوصف" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} data-testid={`quote-item-desc-${i}`} />
                <input className={`${inputCls} w-20`} placeholder="الوحدة" value={it.unit} onChange={(e) => setItem(i, 'unit', e.target.value)} data-testid={`quote-item-unit-${i}`} />
                <input className={`${inputCls} w-20`} dir="ltr" type="number" min="0.01" step="0.01" placeholder="كمية" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} data-testid={`quote-item-qty-${i}`} />
                <input className={`${inputCls} w-28`} dir="ltr" type="number" min="0" step="0.01" placeholder="سعر الوحدة" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} data-testid={`quote-item-price-${i}`} />
                <span className="w-24 text-sm font-semibold text-slate-700" dir="rtl">{money((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0))}</span>
                {form.items.length > 1 && <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="text-red-500" data-testid={`quote-item-remove-${i}`}><Icon name="x" className="w-4 h-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} className="text-xs font-semibold text-teal-700" data-testid="quote-add-item">+ إضافة بند</button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex items-end gap-2">
              <Field label="الخصم">
                <select className={inputCls} value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="quote-input-discount-type">
                  {Object.entries(DISCOUNT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              {form.discount_type && (
                <input className={`${inputCls} w-28`} dir="ltr" type="number" min="0" step="0.01" placeholder={form.discount_type === 'percentage' ? '%' : 'ر.س'} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="quote-input-discount-value" />
              )}
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.tax_enabled} onChange={(e) => setForm({ ...form, tax_enabled: e.target.checked })} data-testid="quote-input-tax-enabled" />
                تطبيق الضريبة
              </label>
              {form.tax_enabled && (
                <input className={`${inputCls} w-24`} dir="ltr" type="number" min="0" max="100" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="quote-input-tax-rate" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="ملاحظات"><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-input-notes" /></Field>
            <Field label="الشروط والأحكام"><textarea rows={2} className={inputCls} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="quote-input-terms" /></Field>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={form.show_bank_details} onChange={(e) => toggleBankDetails(e.target.checked)} data-testid="quote-input-show-bank-details" />
              إظهار بيانات التحويل البنكي في عرض السعر
            </label>
            {form.show_bank_details && (
              <div className="mt-2">
                {bankAccounts.length === 0 ? (
                  <p className="text-xs text-red-600" data-testid="quote-no-bank-accounts">لا توجد حسابات بنكية فعالة — أضف حساباً من إعدادات المنشأة أولاً</p>
                ) : (
                  <Field label="الحساب البنكي" required>
                    <select className={inputCls} value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} data-testid="quote-input-bank-account">
                      <option value="">اختر حساباً</option>
                      {form.bank_account_id && !bankAccounts.find((a) => a.id === form.bank_account_id) && (
                        <option value={form.bank_account_id}>الحساب المحفوظ سابقاً (غير فعال حالياً)</option>
                      )}
                      {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} — {a.iban_formatted || a.iban}{a.is_default ? ' (افتراضي)' : ''}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm" data-testid="quote-totals">
            <div className="flex justify-between"><span className="text-slate-500">المجموع</span><span dir="rtl">{money(totals.subtotal)}</span></div>
            {totals.discountAmount > 0 && <div className="flex justify-between text-amber-700"><span>الخصم</span><span dir="rtl">- {money(totals.discountAmount)}</span></div>}
            {form.tax_enabled && <div className="flex justify-between"><span className="text-slate-500">الضريبة ({form.tax_rate || 0}%)</span><span dir="rtl">{money(totals.taxAmount)}</span></div>}
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-bold"><span>الإجمالي النهائي</span><span dir="rtl">{money(totals.total)}</span></div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={saving} data-testid="quote-form-submit">{saving ? 'جارٍ الحفظ...' : editing ? 'حفظ التعديلات' : 'إنشاء عرض السعر'}</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(delQuote)} title={`حذف عرض السعر ${delQuote?.quote_number || ''}`} onClose={() => setDelQuote(null)}>
        <p className="text-sm text-slate-600">هل أنت متأكد من حذف عرض السعر «{delQuote?.title}»؟ لا يمكن التراجع عن هذا الإجراء.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setDelQuote(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700" data-testid="quote-delete-confirm">تأكيد الحذف</button>
        </div>
      </Modal>

      {viewQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <PrintStyle />
          <div className="absolute inset-0 bg-black/50 print:hidden" onClick={() => setViewQuote(null)} />
          <div className="relative w-full max-w-2xl">
            <div className="print-area max-h-[85vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" data-testid="quote-print-view" ref={printRef}>
              <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-3">
                  {clubInfo.logo_file_id && <img src={`${API}/files/${clubInfo.logo_file_id}`} alt="شعار المنشأة" className="h-12 w-12 rounded-lg object-cover" />}
                  <div>
                    <div className="text-base font-bold text-slate-900">{clubInfo.name || club?.name || 'المنشأة'}</div>
                    <div className="text-xs text-slate-500">{[clubInfo.phone, clubInfo.email, clubInfo.city].filter(Boolean).join(' — ')}</div>
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-teal-700" dir="ltr">{viewQuote.quote_number}</div>
                  <div className="text-xs text-slate-500">{fmtDate(viewQuote.issue_date)}</div>
                  <StatusBadge status={viewQuote.effective_status} />
                </div>
              </div>

              <h3 className="mb-2 text-sm font-bold text-slate-800" data-testid="quote-print-title">{viewQuote.title}</h3>

              <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-sm">
                <div><span className="text-slate-400">العميل / الجهة:</span> <span className="font-semibold">{viewQuote.client?.name}</span></div>
                {viewQuote.client?.phone && <div><span className="text-slate-400">الجوال:</span> {viewQuote.client.phone}</div>}
                {viewQuote.client?.email && <div><span className="text-slate-400">البريد:</span> {viewQuote.client.email}</div>}
                {viewQuote.client?.tax_number && <div><span className="text-slate-400">الرقم الضريبي:</span> <span dir="ltr">{viewQuote.client.tax_number}</span></div>}
              </div>

              <table className="mb-3 w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-right text-xs text-slate-500">
                  <th className="py-1.5">الوصف</th><th className="py-1.5">الوحدة</th><th className="py-1.5">الكمية</th><th className="py-1.5">سعر الوحدة</th><th className="py-1.5">الإجمالي</th>
                </tr></thead>
                <tbody>{(viewQuote.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1.5">{it.description}</td><td className="py-1.5">{it.unit || '—'}</td><td className="py-1.5">{it.quantity}</td>
                    <td className="py-1.5" dir="rtl">{money(it.unit_price)}</td><td className="py-1.5 font-semibold" dir="rtl">{money(it.total)}</td>
                  </tr>
                ))}</tbody>
              </table>

              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">المجموع</span><span dir="rtl">{money(viewQuote.subtotal)}</span></div>
                {viewQuote.discount_amount > 0 && <div className="flex justify-between text-amber-700"><span>الخصم {viewQuote.discount_type === 'percentage' ? `(${viewQuote.discount_value}%)` : ''}</span><span dir="rtl">- {money(viewQuote.discount_amount)}</span></div>}
                {viewQuote.tax_enabled && <div className="flex justify-between"><span className="text-slate-500">الضريبة ({viewQuote.tax_rate}%)</span><span dir="rtl">{money(viewQuote.tax_amount)}</span></div>}
                <div className="flex justify-between border-t border-slate-200 pt-1 text-base font-bold"><span>الإجمالي النهائي</span><span dir="rtl">{money(viewQuote.total)}</span></div>
              </div>

              <p className="mt-3 text-xs text-slate-500" data-testid="quote-print-validity">هذا العرض ساري الصلاحية حتى تاريخ <span className="font-semibold">{fmtDate(viewQuote.expiry_date)}</span></p>
              {viewQuote.notes && <p className="mt-2 text-xs text-slate-500"><span className="font-semibold text-slate-600">ملاحظات: </span>{viewQuote.notes}</p>}
              {viewQuote.terms && <p className="mt-2 text-xs text-slate-500"><span className="font-semibold text-slate-600">الشروط والأحكام: </span>{viewQuote.terms}</p>}

              {viewQuote.show_bank_details && viewQuote.bank_details_snapshot && (
                <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50 p-3 text-xs" data-testid="quote-print-bank-details">
                  <div className="mb-1 text-sm font-bold text-teal-800">بيانات التحويل البنكي</div>
                  <div className="grid grid-cols-2 gap-1">
                    <div><span className="text-slate-500">البنك:</span> <span className="font-semibold">{viewQuote.bank_details_snapshot.bank_name}</span></div>
                    <div><span className="text-slate-500">المستفيد:</span> <span className="font-semibold">{viewQuote.bank_details_snapshot.beneficiary_name}</span></div>
                    <div className="col-span-2"><span className="text-slate-500">الآيبان:</span> <span className="font-semibold" dir="ltr">{formatIban(viewQuote.bank_details_snapshot.iban)}</span></div>
                    {viewQuote.bank_details_snapshot.account_number && <div className="col-span-2"><span className="text-slate-500">رقم الحساب:</span> <span dir="ltr">{viewQuote.bank_details_snapshot.account_number}</span></div>}
                  </div>
                  <p className="mt-2 font-semibold text-teal-700">يرجى كتابة رقم عرض السعر في وصف عملية التحويل.</p>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-800" data-testid="quote-print-disclaimer">
                هذا المستند عرض سعر ولا يُعد فاتورة أو إيصالًا أو إثبات سداد، وتكون صلاحيته حتى التاريخ الموضح أعلاه.
              </div>
            </div>
            <div className="mt-3 flex justify-center gap-2 print:hidden">
              <PrimaryBtn onClick={printArea} data-testid="quote-do-print"><Icon name="document" className="w-4 h-4" /> طباعة</PrimaryBtn>
              <button onClick={async () => { setPdfExporting(true); try { await exportPdf(printRef.current, `عرض-سعر-${viewQuote.quote_number}`); } finally { setPdfExporting(false); } }} disabled={pdfExporting} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-teal-300 disabled:opacity-50" data-testid="quote-download-pdf-btn">{pdfExporting ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="download" className="w-4 h-4" /> تنزيل PDF</>}</button>
              <button onClick={() => setViewQuote(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600">إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
